/**
 * CrossmapProofView — threat-taxonomy proof view backed by the canonical JSON registry.
 *
 * Renders the same structural content previously emitted by the Python HTML
 * generator, but as a reusable React component in the TypeScript presentation
 * layer.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { ColumnDef } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import { GENERATOR_COLOR, GRAY, SAFE, WARN } from "../design-system/tokens.js";
import { useFacetSelection } from "../hooks/useFacetSelection.js";
import { Card } from "./Card.js";
import { VirtualizedDataTable } from "./data/index.js";
import { EmptyState } from "./EmptyState.js";
import { FilterBar, type FilterFacet } from "./FilterBar.js";
import { MarkdownPanel } from "./MarkdownPanel.js";
import { Pill } from "./Pill.js";
import {
  GENERATOR_NAME,
  GENERATOR_ORDER,
  normalizeQuery,
  theoremPresentation,
  type CrossmapGeneratorCode,
} from "./proofViewShared.js";
import { Section } from "./Section.js";

const REGIME_STYLE = {
  1: { color: SAFE[700], bg: SAFE[100], border: SAFE[300], label: "R1" },
  2: { color: WARN[700], bg: "#fef3c7", border: "#fcd34d", label: "R2" },
  3: { color: WARN[700], bg: WARN[100], border: WARN[300], label: "R3" },
} as const;

export interface ThreatCrossmapMeta {
  readonly id: string;
  readonly name: string;
  readonly file: string;
  readonly entry_count: number;
  readonly regime1_percentage: number;
  readonly g2_percentage: number;
  readonly dominant_generator?: CrossmapGeneratorCode;
}

export interface ThreatCrossmapDocument {
  readonly taxonomy: {
    readonly id: string;
    readonly name: string;
    readonly publisher: string;
    readonly version_tracked: string;
    readonly grain: string;
  };
  readonly claim: {
    readonly total_entries: number;
    readonly regime1_count: number;
    readonly regime2_count: number;
    readonly regime3_count: number;
    readonly statement: string;
  };
  readonly aggregate: {
    readonly generator_distribution: Partial<
      Readonly<
        Record<CrossmapGeneratorCode, { readonly count: number; readonly percentage: number }>
      >
    >;
    readonly g2_percentage: number;
    readonly dominant_generator?: CrossmapGeneratorCode;
  };
  readonly entries: readonly ThreatCrossmapEntry[];
  readonly prose_source?: string;
}

export interface ThreatCrossmapEntry {
  readonly id: string;
  readonly name: string;
  readonly generators: readonly CrossmapGeneratorCode[];
  readonly theorems: readonly string[];
  readonly regime: 1 | 2 | 3;
  readonly pattern: string;
  readonly external_ref?: string;
}

export interface CrossmapProofViewProps {
  /** Full crossmap registry document. */
  readonly crossmap: ThreatCrossmapDocument;
  /** Optional meta-registry row for source-file rendering. */
  readonly meta?: ThreatCrossmapMeta;
  /** Optional initial search query. */
  readonly initialSearch?: string;
}

interface StatCardProps {
  readonly label: string;
  readonly value: string | number;
  readonly color: string;
}

interface IndexedThreatCrossmapEntry {
  readonly entry: ThreatCrossmapEntry;
  readonly searchText: string;
}

function crossmapEntrySearchText(entry: ThreatCrossmapEntry): string {
  return [
    entry.id,
    entry.name,
    entry.pattern,
    entry.external_ref ?? "",
    ...entry.theorems,
    ...entry.generators,
  ]
    .join(" ")
    .toLowerCase();
}

const StatCard = React.memo(function StatCard({
  label,
  value,
  color,
}: StatCardProps): React.JSX.Element {
  return (
    <Card
      style={{
        minWidth: "9rem",
        flex: "1 1 9rem",
      }}
    >
      <div style={{ fontSize: "1.875rem", fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: "0.8125rem", color: GRAY[600], marginTop: "0.4rem" }}>{label}</div>
    </Card>
  );
});

/** Render a threat crossmap proof view from the canonical registry document. */
export const CrossmapProofView = React.memo(function CrossmapProofView({
  crossmap,
  meta,
  initialSearch = "",
}: CrossmapProofViewProps): React.JSX.Element {
  const [search, setSearch] = useState(initialSearch);
  const generatorSelection = useFacetSelection<CrossmapGeneratorCode>();

  const query = normalizeQuery(search);
  const indexedEntries = useMemo<readonly IndexedThreatCrossmapEntry[]>(
    () =>
      crossmap.entries.map((entry) => ({
        entry,
        searchText: crossmapEntrySearchText(entry),
      })),
    [crossmap.entries],
  );

  const generatorFacets = useMemo<readonly FilterFacet<CrossmapGeneratorCode>[]>(
    () =>
      GENERATOR_ORDER.map((generator) => ({
        id: generator,
        label: `${generator} ${GENERATOR_NAME[generator]}`,
        count: crossmap.aggregate.generator_distribution[generator]?.count ?? 0,
        color: GENERATOR_COLOR[generator].primary,
      })),
    [crossmap.aggregate.generator_distribution],
  );

  const entryColumns = useMemo<readonly ColumnDef<ThreatCrossmapEntry>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
        cell: ({ row }): React.JSX.Element => (
          <span style={{ fontWeight: 700, color: GENERATOR_COLOR.G2.primary }}>
            {row.original.id}
          </span>
        ),
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        cell: ({ row }): React.JSX.Element => (
          <>
            <div style={{ fontWeight: 600 }}>{row.original.name}</div>
            {row.original.external_ref !== undefined && (
              <div style={{ color: GRAY[500], fontSize: "0.75rem", marginTop: "0.2rem" }}>
                {row.original.external_ref}
              </div>
            )}
          </>
        ),
      },
      {
        id: "generators",
        header: "Generators",
        cell: ({ row }): React.JSX.Element => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {row.original.generators.map((generator) => (
              <Pill
                key={`${row.original.id}-${generator}`}
                color={GENERATOR_COLOR[generator].primary}
                bg={`${GENERATOR_COLOR[generator].primary}12`}
                border={`${GENERATOR_COLOR[generator].primary}33`}
              >
                {generator}
              </Pill>
            ))}
          </div>
        ),
      },
      {
        id: "theorems",
        header: "Theorems",
        cell: ({ row }): React.JSX.Element => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {row.original.theorems.map((theoremCode) => {
              const theorem = theoremPresentation(theoremCode);
              return (
                <Pill
                  key={`${row.original.id}-${theoremCode}`}
                  color={theorem.color}
                  bg={theorem.bg}
                  border={`${theorem.color}33`}
                  title={theorem.title}
                >
                  {theorem.label}
                </Pill>
              );
            })}
          </div>
        ),
      },
      {
        id: "regime",
        header: "Regime",
        cell: ({ row }): React.JSX.Element => {
          const regime = REGIME_STYLE[row.original.regime];
          return (
            <Pill color={regime.color} bg={regime.bg} border={regime.border}>
              {regime.label}
            </Pill>
          );
        },
      },
      {
        id: "pattern",
        header: "Structural pattern",
        accessorKey: "pattern",
        cell: ({ row }): React.JSX.Element => (
          <span style={{ color: GRAY[700] }}>{row.original.pattern}</span>
        ),
      },
    ],
    [],
  );

  const visibleEntries = useMemo(() => {
    const entries: ThreatCrossmapEntry[] = [];
    for (const { entry, searchText } of indexedEntries) {
      const matchesGenerator =
        generatorSelection.active.size === 0 ||
        entry.generators.some((generator) => generatorSelection.active.has(generator));
      if (matchesGenerator && (query === "" || searchText.includes(query))) {
        entries.push(entry);
      }
    }
    return entries;
  }, [generatorSelection.active, indexedEntries, query]);

  const sourceFile = meta?.file ?? `${crossmap.taxonomy.id}.json`;

  return (
    <div
      style={{
        color: GRAY[900],
        display: "grid",
        gap: "1.5rem",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      <header>
        <h1 style={{ margin: 0, fontSize: "2rem", lineHeight: 1.15 }}>{crossmap.taxonomy.name}</h1>
        <MarkdownPanel style={{ color: GRAY[600], marginTop: "0.5rem" }}>
          {`${crossmap.taxonomy.publisher} · ${crossmap.taxonomy.version_tracked} · Grain: ${crossmap.taxonomy.grain}`}
        </MarkdownPanel>
      </header>

      <Card accent={SAFE[500]}>
        <div
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: SAFE[700],
            letterSpacing: "0.06em",
          }}
        >
          COVERAGE CLAIM
        </div>
        <MarkdownPanel style={{ marginTop: "0.5rem" }}>{crossmap.claim.statement}</MarkdownPanel>
      </Card>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        <StatCard label="Regime 1" value={crossmap.claim.regime1_count} color={SAFE[700]} />
        <StatCard label="Regime 2" value={crossmap.claim.regime2_count} color="#d97706" />
        <StatCard label="Regime 3" value={crossmap.claim.regime3_count} color={WARN[700]} />
        <StatCard
          label="Total entries"
          value={crossmap.claim.total_entries}
          color={GENERATOR_COLOR.G2.primary}
        />
        <StatCard
          label="G2 dominance"
          value={`${crossmap.aggregate.g2_percentage}%`}
          color="#7c3aed"
        />
      </div>

      <Section
        title="Generator distribution"
        description="Registry percentages by violated structural generator."
      >
        <Card>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {GENERATOR_ORDER.map((generator) => {
              const bar = crossmap.aggregate.generator_distribution[generator];
              const percentage = bar?.percentage ?? 0;
              const count = bar?.count ?? 0;
              return (
                <div
                  key={generator}
                  style={{
                    display: "grid",
                    gap: "0.5rem",
                    gridTemplateColumns: "9rem 1fr 3rem",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      color: GENERATOR_COLOR[generator].primary,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {generator} {GENERATOR_NAME[generator]}
                  </div>
                  <div
                    aria-label={`${generator} distribution`}
                    style={{
                      backgroundColor: GRAY[100],
                      borderRadius: "9999px",
                      overflow: "hidden",
                      minHeight: "1.5rem",
                    }}
                  >
                    <div
                      style={{
                        alignItems: "center",
                        backgroundColor: GENERATOR_COLOR[generator].primary,
                        color: "white",
                        display: "flex",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        height: "100%",
                        minHeight: "1.5rem",
                        paddingLeft: percentage > 0 ? "0.5rem" : 0,
                        width: `${percentage}%`,
                      }}
                    >
                      {percentage}%
                    </div>
                  </div>
                  <div
                    style={{
                      color: GRAY[700],
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      textAlign: "right",
                    }}
                  >
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </Section>

      <Section
        title="Entries"
        description={`${visibleEntries.length} of ${crossmap.claim.total_entries} mapped entries`}
        action={
          <div style={{ display: "grid", gap: "0.5rem" }}>
            <label style={{ display: "grid", gap: "0.25rem" }}>
              <span style={{ color: GRAY[600], fontSize: "0.75rem", fontWeight: 600 }}>
                Search entries
              </span>
              <input
                aria-label="Search entries"
                type="search"
                value={search}
                onChange={(event): void => {
                  setSearch(event.target.value);
                }}
                placeholder="Search by id, name, or theorem"
                style={{
                  border: `1px solid ${GRAY[300]}`,
                  borderRadius: "0.375rem",
                  color: GRAY[900],
                  fontSize: "0.875rem",
                  minWidth: "16rem",
                  padding: "0.45rem 0.65rem",
                }}
              />
            </label>
          </div>
        }
      >
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <FilterBar
            facets={generatorFacets}
            active={generatorSelection.active}
            onToggle={generatorSelection.toggle}
            onClear={generatorSelection.clear}
            label="Generators"
          />

          {visibleEntries.length === 0 ? (
            <EmptyState
              icon="🔎"
              title="No crossmap entries match the current filters"
              description="Clear the generator filters or broaden the search query."
              action={
                generatorSelection.active.size > 0 || query !== "" ? (
                  <button
                    type="button"
                    onClick={(): void => {
                      generatorSelection.clear();
                      setSearch("");
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: GENERATOR_COLOR.G2.primary,
                      cursor: "pointer",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      textDecoration: "underline",
                    }}
                  >
                    Reset filters
                  </button>
                ) : undefined
              }
            />
          ) : (
            <Card flush aria-label="Crossmap entries table">
              <VirtualizedDataTable
                ariaLabel="Crossmap entries"
                columns={entryColumns}
                data={visibleEntries}
                getRowId={(entry): string => entry.id}
                minWidth="52rem"
                testId="crossmap-entries-table"
              />
            </Card>
          )}
        </div>
      </Section>

      <Card
        footer={
          <div style={{ display: "grid", gap: "0.25rem" }}>
            {crossmap.prose_source !== undefined && (
              <div>
                Prose source: <code>{crossmap.prose_source}</code>
              </div>
            )}
            <div>
              Data source: <code>{sourceFile}</code>
            </div>
          </div>
        }
      >
        <MarkdownPanel>
          {
            "Presentation is rendered in **React/TypeScript** through the open-source `react-markdown` renderer; the JSON registry remains the single source of truth."
          }
        </MarkdownPanel>
      </Card>
    </div>
  );
});
